import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Play, ChevronLeft, ChevronRight, CheckCircle, Circle,
  Download, FileText, Clock, Sparkles, BookOpen, Award, ArrowRight
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import CoursePracticeQuiz from '../../components/Student/CoursePracticeQuiz';

const STAGES = [
  { id: 0, code: 'A', key: 'stepA_Why', label: '1. Why', stepCode: 'A_Why', icon: '🎯', desc: 'Why this matters' },
  { id: 1, code: 'B', key: 'stepB_Story', label: '2. Story', stepCode: 'B_Story', icon: '📖', desc: 'The narrative story' },
  { id: 2, code: 'C', key: 'stepC_Framework', label: '3. Framework', stepCode: 'C_Framework', icon: '🧩', desc: 'Core concept framework' },
  { id: 3, code: 'D', key: 'stepD_Practice', label: '4. Practice', stepCode: 'D_Practice', icon: '⚙️', desc: 'Interactive exercise' },
  { id: 4, code: 'E', key: 'stepE_FlashCard', label: '5. Flash Cards', stepCode: 'E_FlashCard', icon: '🃏', desc: 'Reinforcement cards' },
  { id: 5, code: 'F', key: 'stepF_AdvancedPractice', label: '6. Advanced Practice', stepCode: 'F_AdvancedPractice', icon: '🔬', desc: 'Deeper reflection' },
  { id: 6, code: 'G', key: 'stepG_CaseStudy', label: '7. Case Study', stepCode: 'G_CaseStudy', icon: '💼', desc: 'Scenario & reflection' },
  { id: 7, code: 'H', key: 'stepH_Notes', label: '8. Notes', stepCode: 'H_Notes', icon: '📝', desc: 'Personal notes' }
];

const TEMPORARY_VIDEO = 'https://res.cloudinary.com/dcppk0wx4/video/upload/v1/samples/elephants.mp4';

const CoursePlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Legacy states
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentDay, setCurrentDay] = useState(1);
  const [expandedSteps, setExpandedSteps] = useState({});

  // 7-Stage Learning Flow states
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [reflectionAnswer, setReflectionAnswer] = useState('');
  const [practiceQuizDone, setPracticeQuizDone] = useState(false);
  const [advancedAnswer, setAdvancedAnswer] = useState('');
  const [caseStudyAnswer, setCaseStudyAnswer] = useState('');
  const [personalNotes, setPersonalNotes] = useState('');
  
  // Flashcard states
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [viewedCards, setViewedCards] = useState(new Set());

  const fetchCourseAndEnrollment = useCallback(async () => {
    try {
      setLoading(true);
      const courseRes = await api.get(`/courses/${id}`);
      if (courseRes.data.success) {
        setCourse(courseRes.data.data);
      }
      const enrollmentRes = await api.get('/enrollments/my-courses');
      if (enrollmentRes.data.success) {
        const myEnrollment = enrollmentRes.data.data.find(e => e.course._id === id);
        setEnrollment(myEnrollment);
        
        // Populate inputs if submission exists
        const practiceStep = myEnrollment?.learningFlowProgress?.find(s => s.stepCode === 'D_Practice');
        if (practiceStep?.submission?.answers?.length) {
          setReflectionAnswer(practiceStep.submission.answers[0] || '');
        }

        const advancedStep = myEnrollment?.learningFlowProgress?.find(s => s.stepCode === 'F_AdvancedPractice');
        if (advancedStep?.submission?.answers?.length) {
          setAdvancedAnswer(advancedStep.submission.answers[0] || '');
        }

        const caseStudyStep = myEnrollment?.learningFlowProgress?.find(s => s.stepCode === 'G_CaseStudy');
        if (caseStudyStep?.submission?.answers?.length) {
          setCaseStudyAnswer(caseStudyStep.submission.answers[0] || '');
        }

        const notesStep = myEnrollment?.learningFlowProgress?.find(s => s.stepCode === 'H_Notes');
        if (notesStep?.submission?.answers?.length) {
          setPersonalNotes(notesStep.submission.answers[0] || '');
        }
      }
    } catch (error) {
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCourseAndEnrollment();
  }, [fetchCourseAndEnrollment]);

  const isStepCompleted = (stepCode) => {
    return !!enrollment?.learningFlowProgress?.find(s => s.stepCode === stepCode)?.isCompleted;
  };

  const handleCompleteStep = async (stepCode, submissionData = null) => {
    if (!enrollment) return;
    try {
      setActionLoading(true);
      const response = await api.post('/programme/complete-learning-step', {
        enrollmentId: enrollment._id,
        stepCode,
        submission: submissionData
      });

      if (response.data.success) {
        toast.success('Stage completed successfully!');
        const flowDone = response.data.data.learningFlowCompleted;
        setEnrollment(prev => ({
          ...prev,
          learningFlowProgress: response.data.data.progress,
          learningFlowCompleted: flowDone
        }));
        if (flowDone) {
          toast.success('Course complete! Take the micro assessment from your dashboard.', { duration: 5000 });
        }
        
        // Auto-advance if not the last stage
        if (activeStepIndex < STAGES.length - 1) {
          setActiveStepIndex(prev => prev + 1);
          setCardFlipped(false);
          setCurrentCardIndex(0);
          setViewedCards(new Set());
        }
      }
    } catch (error) {
      toast.error('Failed to complete stage. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#00152E]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!course) return <div className="text-center py-20 text-red-500 font-bold">Course not found</div>;

  const isLearningFlow = !!course.learningFlow;

  // ─── 1. RENDER 7-STAGE LEARNING FLOW PLAYER ─────────────────────────────────
  if (isLearningFlow) {
    const activeStage = STAGES[activeStepIndex];
    const stepData = course.learningFlow[activeStage.key] || {};
    const stepCompleted = isStepCompleted(activeStage.stepCode);
    const completedCount = enrollment?.learningFlowProgress?.filter(s => s.isCompleted).length || 0;
    const progressPercent = Math.round((completedCount / STAGES.length) * 100);

    return (
      <div className="flex h-screen bg-[#F8FAFC]">
        {/* Left Sidebar */}
        <div className="w-80 bg-white border-r border-slate-200 overflow-y-auto flex flex-col justify-between">
          <div>
            {/* Header */}
            <div className="p-6 border-b border-slate-200 bg-gradient-to-br from-[#00152E] to-[#1a3884] text-white">
              <button
                onClick={() => navigate('/student/my-courses')}
                className="flex items-center text-xs text-indigo-200 hover:text-white mb-4 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Dashboard
              </button>
              <span className="text-[10px] font-bold tracking-widest uppercase bg-indigo-500/30 text-indigo-300 px-2 py-0.5 rounded-full">
                {course.courseNumber} · {course.category}
              </span>
              <h2 className="text-lg font-bold mt-2 leading-tight">{course.title}</h2>
              
              <div className="mt-4">
                <div className="flex justify-between text-xs font-bold text-indigo-200 mb-1">
                  <span>Course Progress</span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="w-full bg-indigo-900/50 rounded-full h-2">
                  <div className="bg-indigo-400 h-2 rounded-full transition-all duration-300" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            </div>

            {/* Stages List */}
            <div className="p-4 space-y-1">
              <h3 className="text-xs font-bold text-slate-400 uppercase px-3 mb-2 tracking-wider">Learning Journey</h3>
              {STAGES.map((s, idx) => {
                const isActive = activeStepIndex === idx;
                const done = isStepCompleted(s.stepCode);
                return (
                  <button
                    key={s.key}
                    onClick={() => {
                      setActiveStepIndex(idx);
                      setCardFlipped(false);
                      setCurrentCardIndex(0);
                      setViewedCards(new Set());
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all border ${
                      isActive
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-900 font-bold'
                        : 'bg-transparent border-transparent text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{s.icon}</span>
                      <div className="text-left">
                        <p className="text-xs font-bold leading-none mb-0.5">{s.label}</p>
                        <p className="text-[10px] text-slate-400 font-normal">{s.desc}</p>
                      </div>
                    </div>
                    {done ? (
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer Card */}
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-700">Assessment</p>
                <p className="text-[10px] text-slate-500">Unlock at 100% completion</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-slate-50/50">
          <div className="max-w-4xl mx-auto p-8">
            
            {/* Celebration or Next Stage Banner */}
            {enrollment.learningFlowCompleted && (
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-3xl">🎉</span>
                  <div>
                    <h3 className="font-black text-lg">7-Stage Learning Flow Completed!</h3>
                    <p className="text-emerald-100 text-xs mt-1">Excellent work. You have finished all core modules for {course.title}.</p>
                  </div>
                </div>
                <button
                  onClick={() => toast('Gate assessments can be started from the course dashboard.')}
                  className="bg-white text-emerald-700 font-black text-xs px-4 py-2.5 rounded-xl hover:bg-emerald-50 shadow transition"
                >
                  Return to Dashboard
                </button>
              </div>
            )}

            {/* Stage Workspace */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden mb-6">
              <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{activeStage.icon}</span>
                  <div>
                    <h1 className="text-xl font-black text-slate-800">{activeStage.label.toUpperCase()}: {stepData.title || activeStage.desc}</h1>
                    <p className="text-xs text-slate-500">Stage {activeStepIndex + 1} of {STAGES.length}</p>
                  </div>
                </div>
                {stepCompleted && (
                  <span className="flex items-center gap-1 text-xs font-bold bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full border border-emerald-200">
                    <CheckCircle className="w-3.5 h-3.5" /> Stage Completed
                  </span>
                )}
              </div>

              <div className="p-6">
                
                {/* ── A_Why & B_Story: Video Players ── */}
                {(activeStage.key === 'stepA_Why' || activeStage.key === 'stepB_Story') && (
                  <div className="space-y-6">
                    <div className="aspect-video bg-black rounded-2xl overflow-hidden relative shadow-inner border border-slate-900">
                      <video
                        src={stepData.videoUrl || TEMPORARY_VIDEO}
                        controls
                        className="w-full h-full"
                      />
                      {!stepData.videoUrl && (
                        <div className="absolute top-4 left-4 bg-indigo-950/80 backdrop-blur text-indigo-300 border border-indigo-800 text-[10px] font-black px-3 py-1.5 rounded-xl flex items-center gap-1.5 shadow-lg">
                          <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Temporary Video Demonstration
                        </div>
                      )}
                    </div>

                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                      <h4 className="font-bold text-slate-800 text-sm mb-2">Stage Narrative & Key Insights</h4>
                      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                        {activeStage.key === 'stepA_Why' ? stepData.text : stepData.narrative}
                      </p>
                    </div>

                    {!stepCompleted && (
                      <button
                        onClick={() => handleCompleteStep(activeStage.stepCode)}
                        disabled={actionLoading}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md shadow-indigo-600/10 flex items-center justify-center gap-2"
                      >
                        {actionLoading ? 'Saving Stage...' : 'Complete Stage & Continue'} <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* ── C_Framework: Conceptual Content ── */}
                {activeStage.key === 'stepC_Framework' && (
                  <div className="space-y-6">
                    {stepData.diagramUrl && (
                      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm max-h-96 flex items-center justify-center bg-slate-50">
                        <img src={stepData.diagramUrl} alt="Concept Framework" className="object-contain w-full h-full" />
                      </div>
                    )}

                    <div className="prose prose-slate max-w-none text-slate-600 text-sm leading-relaxed" 
                         dangerouslySetInnerHTML={{ __html: stepData.content }} />

                    {stepData.keyPoints?.length > 0 && (
                      <div className="bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100/50">
                        <h4 className="font-bold text-indigo-950 text-sm mb-3 flex items-center gap-1.5">
                          <BookOpen className="w-4 h-4 text-indigo-600" /> Key Takeaways
                        </h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {stepData.keyPoints.map((pt, pIdx) => (
                            <li key={pIdx} className="flex gap-2 text-xs text-indigo-900/90 font-medium">
                              <span className="text-indigo-600">•</span>
                              {pt}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!stepCompleted && (
                      <button
                        onClick={() => handleCompleteStep(activeStage.stepCode)}
                        disabled={actionLoading}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md shadow-indigo-600/10"
                      >
                        {actionLoading ? 'Saving...' : 'I Understand the Framework'}
                      </button>
                    )}
                  </div>
                )}

                {/* ── D_Practice: Active Tasks ── */}
                {activeStage.key === 'stepD_Practice' && (
                  <div className="space-y-6">
                    {stepData.instructions && (
                      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        <h4 className="font-bold text-slate-800 text-sm mb-2">Practice Instructions</h4>
                        <p className="text-slate-600 text-sm leading-relaxed">{stepData.instructions}</p>
                      </div>
                    )}

                    {stepData.activityType === 'quiz' ? (
                      <>
                        <CoursePracticeQuiz
                          courseId={id}
                          instructions={stepData.instructions}
                          disabled={stepCompleted || actionLoading}
                          onQuizComplete={() => setPracticeQuizDone(true)}
                        />
                        {!stepCompleted && (
                          <button
                            onClick={() => handleCompleteStep(activeStage.stepCode, { answers: ['micro-quiz-completed'] })}
                            disabled={actionLoading || !practiceQuizDone}
                            className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md"
                          >
                            {actionLoading ? 'Saving…' : practiceQuizDone ? 'Complete Practice Step' : 'Submit the quiz first'}
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="space-y-3">
                          <label className="block text-xs font-bold text-slate-500 uppercase">Your Practice Submission</label>
                          <textarea
                            value={reflectionAnswer}
                            onChange={(e) => setReflectionAnswer(e.target.value)}
                            placeholder="Write down your actions, answers, or learnings from this practice session..."
                            disabled={stepCompleted || actionLoading}
                            rows={5}
                            className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-400 bg-slate-50/20 disabled:bg-slate-50"
                          />
                        </div>
                        {!stepCompleted && (
                          <button
                            onClick={() => handleCompleteStep(activeStage.stepCode, { answers: [reflectionAnswer] })}
                            disabled={actionLoading || !reflectionAnswer.trim()}
                            className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md"
                          >
                            {actionLoading ? 'Submitting Practice...' : 'Submit Practice Response'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── E_FlashCard: Core Flash Cards ── */}
                {activeStage.key === 'stepE_FlashCard' && (
                  <div className="space-y-6">
                    {stepData.cards?.length > 0 ? (
                      <div className="flex flex-col items-center py-6">
                        
                        {/* 3D Flip Card */}
                        <div 
                          onClick={() => {
                            setCardFlipped(prev => !prev);
                            setViewedCards(prev => {
                              const updated = new Set(prev);
                              updated.add(currentCardIndex);
                              return updated;
                            });
                          }}
                          className="w-full max-w-md h-64 cursor-pointer relative preserve-3d duration-500 transition-all select-none"
                          style={{
                            transform: cardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                            transformStyle: 'preserve-3d'
                          }}
                        >
                          {/* Front Side */}
                          <div 
                            className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-6 text-white flex flex-col justify-between shadow-xl"
                            style={{ backfaceVisibility: 'hidden' }}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-0.5 rounded-full w-max">
                              Question Card
                            </span>
                            <p className="text-lg font-black text-center leading-relaxed">
                              {stepData.cards[currentCardIndex]?.front}
                            </p>
                            <span className="text-center text-[10px] font-bold opacity-60">
                              Click Card to Reveal Answer
                            </span>
                          </div>

                          {/* Back Side */}
                          <div 
                            className="absolute inset-0 bg-white border-2 border-indigo-500 rounded-2xl p-6 text-indigo-900 flex flex-col justify-between shadow-xl backface-hidden"
                            style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full w-max">
                              Answer
                            </span>
                            <p className="text-base font-bold text-center leading-relaxed">
                              {stepData.cards[currentCardIndex]?.back}
                            </p>
                            <span className="text-center text-[10px] text-slate-400 font-bold">
                              Click to Show Question
                            </span>
                          </div>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-6 mt-6">
                          <button
                            disabled={currentCardIndex === 0}
                            onClick={() => {
                              setCurrentCardIndex(prev => prev - 1);
                              setCardFlipped(false);
                            }}
                            className="p-2 border border-slate-200 hover:bg-slate-100 disabled:opacity-30 rounded-xl transition"
                          >
                            <ChevronLeft className="w-5 h-5 text-slate-600" />
                          </button>
                          <span className="text-xs font-bold text-slate-600">
                            Card {currentCardIndex + 1} of {stepData.cards.length}
                          </span>
                          <button
                            disabled={currentCardIndex === stepData.cards.length - 1}
                            onClick={() => {
                              setCurrentCardIndex(prev => prev + 1);
                              setCardFlipped(false);
                            }}
                            className="p-2 border border-slate-200 hover:bg-slate-100 disabled:opacity-30 rounded-xl transition"
                          >
                            <ChevronRight className="w-5 h-5 text-slate-600" />
                          </button>
                        </div>

                        {/* Complete Actions */}
                        {!stepCompleted && (
                          <div className="w-full mt-6">
                            <button
                              onClick={() => handleCompleteStep(activeStage.stepCode)}
                              disabled={actionLoading || viewedCards.size < stepData.cards.length}
                              className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md"
                            >
                              {viewedCards.size < stepData.cards.length
                                ? `Flip remaining cards (${viewedCards.size}/${stepData.cards.length}) to complete`
                                : 'Complete Flashcards Stage'}
                            </button>
                          </div>
                        )}

                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">No flashcards configured for this stage.</div>
                    )}
                  </div>
                )}

                {/* ── F_AdvancedPractice: Deep Reflector ── */}
                {activeStage.key === 'stepF_AdvancedPractice' && (
                  <div className="space-y-6">
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                      <h4 className="font-bold text-slate-800 text-sm mb-2">Instructions</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{stepData.instructions}</p>
                    </div>

                    {stepData.questions?.map((q, qIdx) => (
                      <div key={qIdx} className="space-y-3">
                        <label className="block text-sm font-bold text-slate-800">{q.question}</label>
                        <textarea
                          value={advancedAnswer}
                          onChange={(e) => setAdvancedAnswer(e.target.value)}
                          placeholder="Provide a detailed reflection response..."
                          disabled={stepCompleted || actionLoading}
                          rows={6}
                          className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-400 bg-slate-50/20 disabled:bg-slate-50"
                        />
                      </div>
                    ))}

                    {!stepCompleted && (
                      <button
                        onClick={() => handleCompleteStep(activeStage.stepCode, { answers: [advancedAnswer] })}
                        disabled={actionLoading || !advancedAnswer.trim()}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md"
                      >
                        {actionLoading ? 'Submitting Reflection...' : 'Complete Advanced Stage'}
                      </button>
                    )}
                  </div>
                )}

                {/* ── G_CaseStudy: Scenario & Reflection ── */}
                {activeStage.key === 'stepG_CaseStudy' && (
                  <div className="space-y-6">
                    {(stepData.caseTitle || stepData.caseText) && (
                      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        {stepData.caseTitle && (
                          <h4 className="font-bold text-slate-800 text-sm mb-2">{stepData.caseTitle}</h4>
                        )}
                        {stepData.caseText && (
                          <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{stepData.caseText}</p>
                        )}
                      </div>
                    )}

                    {stepData.questions?.length > 0 && (
                      <div className="space-y-3">
                        {stepData.questions.map((q, qIdx) => (
                          <label key={qIdx} className="block text-sm font-bold text-slate-800">
                            {qIdx + 1}. {q.question || q.questionText}
                          </label>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      <label className="block text-xs font-bold text-slate-500 uppercase">Your Response</label>
                      <textarea
                        value={caseStudyAnswer}
                        onChange={(e) => setCaseStudyAnswer(e.target.value)}
                        placeholder="Analyze the case study and respond to the reflection question(s) above..."
                        disabled={stepCompleted || actionLoading}
                        rows={6}
                        className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-400 bg-slate-50/20 disabled:bg-slate-50"
                      />
                    </div>

                    {!stepCompleted && (
                      <button
                        onClick={() => handleCompleteStep(activeStage.stepCode, { answers: [caseStudyAnswer] })}
                        disabled={actionLoading || !caseStudyAnswer.trim()}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md"
                      >
                        {actionLoading ? 'Submitting Case Study...' : 'Complete Case Study'}
                      </button>
                    )}
                  </div>
                )}

                {/* ── H_Notes: Final Notebook ── */}
                {activeStage.key === 'stepH_Notes' && (
                  <div className="space-y-6">
                    <div className="bg-indigo-50/30 p-6 rounded-2xl border border-indigo-100/50">
                      <h4 className="font-bold text-indigo-900 text-sm mb-2">Notes & Takeaways Prompt</h4>
                      <p className="text-indigo-850 text-sm leading-relaxed">{stepData.prompt}</p>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-xs font-bold text-slate-500 uppercase">Your Notes</label>
                      <textarea
                        value={personalNotes}
                        onChange={(e) => setPersonalNotes(e.target.value)}
                        placeholder="Write down your key learnings, key terms, or general thoughts..."
                        disabled={stepCompleted || actionLoading}
                        rows={8}
                        className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-400 bg-slate-50/20 disabled:bg-slate-50"
                      />
                    </div>

                    {!stepCompleted && (
                      <button
                        onClick={() => handleCompleteStep(activeStage.stepCode, { answers: [personalNotes] })}
                        disabled={actionLoading}
                        className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black rounded-xl text-sm transition-all shadow-md"
                      >
                        {actionLoading ? 'Saving Notes...' : 'Save Notes & Complete Course Flow'}
                      </button>
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* Step navigation bar */}
            <div className="flex justify-between items-center bg-white px-6 py-4 rounded-xl border border-slate-200">
              <button
                disabled={activeStepIndex === 0}
                onClick={() => {
                  setActiveStepIndex(prev => prev - 1);
                  setCardFlipped(false);
                }}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 hover:bg-slate-50 disabled:opacity-30 rounded-xl text-xs font-bold text-slate-600 transition"
              >
                <ChevronLeft className="w-4 h-4" /> Previous Stage
              </button>
              <span className="text-xs font-bold text-slate-500">
                Stage {activeStepIndex + 1} of {STAGES.length}
              </span>
              <button
                disabled={activeStepIndex === STAGES.length - 1}
                onClick={() => {
                  setActiveStepIndex(prev => prev + 1);
                  setCardFlipped(false);
                }}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 hover:bg-slate-50 disabled:opacity-30 rounded-xl text-xs font-bold text-slate-600 transition"
              >
                Next Stage <ChevronRight className="w-4 h-4" />
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ─── 2. RENDER LEGACY MODULES/DAYS COURSE PLAYER ─────────────────────────────
  const currentModule = course?.modules?.[currentModuleIndex];
  const days = currentModule?.days || [];
  const activeDayContent = days.find(d => d.dayNumber === currentDay);

  const toggleStep = (stepNumber) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepNumber]: !prev[stepNumber]
    }));
  };

  const isModuleCompleted = (moduleIndex) => {
    return enrollment?.progress?.completedModules?.some(
      m => m.module.toString() === course.modules[moduleIndex]._id.toString()
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Module Navigation */}
      <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <button
            onClick={() => navigate('/student/my-courses')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to My Courses
          </button>
          <h2 className="text-lg font-bold text-gray-900">{course.title}</h2>
          <div className="mt-2 flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-1" />
            {course.duration} hours
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Skills / Modules</h3>
          {course.modules.map((module, index) => (
            <button
              key={module._id || index}
              onClick={() => {
                setCurrentModuleIndex(index);
                setCurrentDay(1);
                setExpandedSteps({});
              }}
              className={`w-full text-left p-4 rounded-lg mb-2 transition-colors ${currentModuleIndex === index
                ? 'bg-blue-50 border-2 border-blue-500'
                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {isModuleCompleted(index) ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-sm font-medium text-gray-900">Module {index + 1}</span>
                  </div>
                  <p className="text-sm text-gray-600 ml-7">{module.title}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {currentModule?.title}
            </h1>
            <p className="text-gray-600">{currentModule?.description}</p>
          </div>

          {/* Day Tabs */}
          <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
            {[1, 2, 3].map((dayNum) => (
              <button
                key={dayNum}
                onClick={() => setCurrentDay(dayNum)}
                className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${currentDay === dayNum
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
              >
                Day {dayNum}
              </button>
            ))}
          </div>

          {/* Steps Content */}
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Day {currentDay}: {activeDayContent?.title || 'Course Content'}
                </h2>
                <p className="text-gray-500">{activeDayContent?.description}</p>
              </div>
            </div>

            {activeDayContent?.steps?.map((step, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleStep(idx)}
                  className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step.type === 'video' ? 'bg-red-100 text-red-600' :
                      step.type === 'quiz' ? 'bg-purple-100 text-purple-600' :
                      step.type === 'submission' ? 'bg-green-100 text-green-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {step.type === 'video' ? <Play className="w-5 h-5 ml-1" /> : <FileText className="w-5 h-5" />}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">
                        Step {step.stepNumber}: {step.title}
                      </h3>
                      <p className="text-sm text-gray-500 capitalize">{step.type}</p>
                    </div>
                  </div>
                  {expandedSteps[idx] ? <ChevronLeft className="w-5 h-5 rotate-270" /> : <ChevronRight className="w-5 h-5" />}
                </button>

                {expandedSteps[idx] && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-6">
                    <p className="mb-4 text-gray-700 bg-gray-50 p-3 rounded">{step.content?.description}</p>

                    {/* Video Player */}
                    {step.type === 'video' && step.content?.videoUrl && (
                      <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                        <video
                          src={step.content.videoUrl}
                          controls
                          className="w-full h-full"
                        />
                      </div>
                    )}

                    {/* Quiz */}
                    {(step.type === 'quiz' || step.type === 'reflection') && (
                      <div className="space-y-4">
                        {step.content?.questions?.map((q, qIdx) => (
                          <div key={qIdx} className="border border-gray-200 rounded-lg p-4">
                            <p className="font-medium mb-3">{qIdx + 1}. {q.question}</p>
                            {step.type === 'quiz' && (
                              <div className="space-y-2">
                                {q.options?.map((opt, oIdx) => (
                                  <label key={oIdx} className="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer">
                                    <input type="radio" name={`q-${idx}-${qIdx}`} className="mr-3" />
                                    <span>{opt}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {step.type === 'reflection' && (
                              <textarea
                                className="w-full border border-gray-300 rounded-lg p-3"
                                rows={3}
                                placeholder="Your answer..."
                              />
                            )}
                          </div>
                        ))}
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Submit</button>
                      </div>
                    )}

                    {/* Submission */}
                    {step.type === 'submission' && (
                      <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                        <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                        <p className="mb-2 text-gray-900 font-medium">Upload Evidence</p>
                        <p className="text-sm text-gray-500 mb-4">{step.content?.instructions}</p>
                        <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                          Choose File
                        </button>
                      </div>
                    )}

                    {/* Flashcard */}
                    {step.type === 'flashcard' && (
                      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6 text-center">
                        <h4 className="font-bold text-indigo-900 mb-2">{step.title}</h4>
                        <p className="text-indigo-700 mb-4">Review the flashcard material.</p>
                        {step.content?.fileUrl && (
                          <a href={step.content.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-indigo-600 font-medium hover:underline">
                            <Download className="w-4 h-4 mr-2" />
                            View/Download Resource
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {(!activeDayContent || !activeDayContent.steps || activeDayContent.steps.length === 0) && (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-500">No content available for this day.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;
